<template>
  <v-container class="profile-editor-container" fluid>
    <v-row v-if="user" class="profile-editor-layout">
      <v-col cols="12" class="avatar-upload text-center" v-show="!edit">
        <v-avatar
          v-if="user.url && !files.length"
          size="156"
          v-ripple
          class="mb-3"
        >
          <label for="avatar">
            <v-img :src="user.url" alt="avatar" cover class="rounded-circle" />
          </label>
        </v-avatar>

        <v-avatar v-else-if="files.length" size="156" v-ripple class="mb-3">
          <label for="avatar">
            <v-img
              :src="files[0].url"
              alt="avatar"
              cover
              class="rounded-circle"
            />
          </label>
        </v-avatar>

        <v-avatar v-else size="156" v-ripple class="bg-grey-lighten-3 mb-3">
          <label for="avatar" @click="initFileUpload">
            <span>{{ $t('profile.clickToAddImage') }}</span>
          </label>
        </v-avatar>

        <v-col cols="12" class="text-center pb-2">
          <v-btn
            variant="flat"
            color="#A5D867"
            class="text-white font-weight-bold"
            style="text-transform: uppercase"
          >
            <BaseFileUpload
              :extensions="'gif,jpg,jpeg,png,webp'"
              :accept="'image/png,image/gif,image/jpeg,image/webp'"
              name="avatar"
              v-model="files"
              :inputFilter="inputFilter"
              class="green-btn v-btn v-size--default text-white font-weight-bold"
              ref="upload"
            >
              <span v-if="user?.url || files.length">{{
                $t('profile.changeAvatar')
              }}</span>
              <span v-else class="v-btn__content">{{
                $t('profile.selectImage')
              }}</span>
            </BaseFileUpload>
          </v-btn>

          <v-btn
            v-if="files.length"
            color="#592892"
            ripple
            variant="flat"
            class="ml-3"
            @click.prevent="reset"
          >
            {{ $t('common.cancel') }}
          </v-btn>

          <v-col cols="10" offset="1" v-if="avatar && !saved && uploadStarted">
            <v-row class="progress-container">
              <v-col
                cols="12"
                class="progress-text d-flex justify-space-between"
              >
                <span>{{ $t('profile.uploadProgress') }}</span>
                <span>{{ progressUpload }}%</span>
              </v-col>
              <v-col cols="12">
                <v-progress-linear v-model="progressUpload" color="#A0C548" />
              </v-col>
            </v-row>
          </v-col>
        </v-col>

        <v-btn
          v-if="files.length"
          :loading="loading || creating"
          :disabled="loading || creating"
          ripple
          color="#a5d867"
          class="text-white font-weight-bold"
          :class="{ 'pulse-button': !loading }"
          @click="setImage"
        >
          {{ $t('profile.uploadImage') }}
          <template #loader>
            {{ $t('profile.uploading') }}
            <span class="custom-loader">
              <v-icon>cached</v-icon>
            </span>
          </template>
        </v-btn>
      </v-col>

      <v-col cols="12" class="avatar-edit pb-2" v-show="files.length && edit">
        <v-col cols="12" class="avatar-edit-image" v-if="files.length">
          <img ref="editImage" :src="files[0].url" />
        </v-col>
      </v-col>

      <v-col cols="12" class="pa-0">
        <v-alert
          v-if="files.length && !saved"
          :value="true"
          density="compact"
          text
          color="#592892"
        >
          <span v-html="$t('profile.makeSureToClick')"></span>
        </v-alert>
      </v-col>

      <v-col cols="12" v-if="!e1" class="pb-0">
        <p>{{ $t('profile.addYourName') }}</p>
      </v-col>

      <v-col :cols="e1 ? 12 : 10" :offset="e1 ? 0 : 1" class="pb-0">
        <v-form v-model="isFormValid">
          <v-text-field
            v-model="user_name"
            :rules="[rules.required, rules.length(2)]"
            counter
            maxlength="30"
            :label="$t('profile.yourName')"
            class="user-input"
            variant="underlined"
            color="#A0C548"
          />
        </v-form>
      </v-col>

      <v-col cols="12" class="text-center">
        <v-btn
          v-if="files.length === 0"
          :loading="creating"
          :disabled="creating || !isFormValid"
          ripple
          variant="flat"
          color="#a5d867"
          class="mb-4 text-white font-weight-bold"
          @click="updateProfile"
        >
          {{ $t('profile.updateProfile') }}
          <template #loader>
            {{ $t('profile.updating') }}
            <span class="custom-loader">
              <v-icon>cached</v-icon>
            </span>
          </template>
        </v-btn>
      </v-col>

      <v-col cols="12" class="text-center pa-0" v-if="user">
        <v-btn
          v-if="!hasPhoneNumber"
          @click="openPhoneDialog"
          class="rounded-1 font-weight-bold text-grey-darken-1 text-capitalize text-subtitle-1"
          elevation="1"
          color="#fff"
          height="50"
        >
          <v-icon class="mr-4" color="#A5D867" icon="phoneicon" />
          {{ $t('profile.linkPhoneToAccount') }}
        </v-btn>
        <v-card-text v-else class="text-h6 pa-0">
          <strong>{{ user.phoneNumber }}</strong>
          <p class="text-subtitle-1 mb-0">
            {{ $t('profile.isLinkedToAccount') }}
          </p>
          <v-btn
            @click="unLinkPhoneDialog = true"
            class="rounded-1 font-weight-bold white-text text-capitalize text-subtitle-1 mt-4"
            elevation="1"
            color="rgba(56, 57, 53, 0.75)"
          >
            <v-icon class="mr-4" color="#A5D867">phone</v-icon>
            {{ $t('profile.unlinkPhone') }}
          </v-btn>
        </v-card-text>
      </v-col>

      <v-col
        v-if="showLeaderRequest"
        cols="12"
        class="text-center pa-0"
      >
        <v-btn
          v-if="canRequestLeader"
          :loading="requestingLeader"
          :disabled="requestingLeader"
          @click="requestLeader"
          class="leader-request-btn font-weight-bold text-uppercase mt-6"
          variant="outlined"
          color="#592892"
          height="44"
        >
          <v-icon class="mr-2" size="20">mdi-account-star-outline</v-icon>
          {{ $t('profile.leaderRequest.button') }}
        </v-btn>
        <v-btn
          v-else
          disabled
          class="leader-request-btn font-weight-bold text-uppercase mt-6"
          variant="outlined"
          color="#592892"
          height="44"
        >
          <v-icon class="mr-2" size="20">mdi-account-check-outline</v-icon>
          {{ $t('profile.leaderRequest.sent') }}
        </v-btn>
      </v-col>

      <v-col :cols="e1 ? 12 : 10" :offset="e1 ? 0 : 1">
        <v-alert
          v-if="feedback"
          :value="true"
          color="#592892"
          class="text-center font-weight-bold mb-1"
          density="compact"
          border="start"
        >
          <span v-html="feedback"></span>
        </v-alert>
      </v-col>
    </v-row>
    <!-- Phone Linking Dialog -->
    <v-dialog v-model="phoneDialog" max-width="400px">
      <v-card
        :color="$vuetify.theme.dark ? '#2d2d2d' : 'rgba(255,255,255,.95)'"
      >
        <v-card-title class="text-h6 pb-0 justify-center">
          {{ $t('profile.linkPhoneToAccount') }}
        </v-card-title>

        <div v-if="verifyPhone">
          <v-card-actions>
            <v-text-field
              class="sign-in-with-phone"
              variant="outlined"
              density="compact"
              :placeholder="$t('loginScreen.verificationCode')"
              v-model="otp"
            />
          </v-card-actions>
          <v-card-actions>
            <v-btn
              class="text-white font-weight-bold text-subtitle-2 mx-auto mb-4"
              variant="flat"
              color="#A5D867"
              @click="submitPhoneNumberAuthCode(otp)"
            >
              {{ $t('loginScreen.enterCode') }}
            </v-btn>
          </v-card-actions>
        </div>

        <div v-else>
          <v-card-text class="phone-login-form">
            <v-form ref="form" v-model="valid" @submit.prevent>
              <!-- <vue-tel-input-vuetify
                v-model="phone"
                clearable
                required
                :default-country="'us'"
                :inputoptions="{ showDialCode: true }"
                :rules="[(v) => phoneIsValid || 'Enter a valid phone number']"
                label="Phone Number"
                @blur="validatePhone"
              /> -->
              <v-phone-input
                v-model="phone"
                :rules="getPhoneRules()"
                :label="$t('loginScreen.phoneLabel')"
                :country-label="$t('loginScreen.countryLabel')"
                :country-aria-label="$t('loginScreen.countryLabel')"
                :invalid-message="$t('loginScreen.phoneInvalid')"
                variant="outlined"
                density="comfortable"
                filled
                show-country-code
                default-country="US"
                clearable
                required
              />
            </v-form>

            <v-card-actions>
              <v-btn
                class="text-white font-weight-bold text-subtitle-2 mx-auto mt-4"
                variant="flat"
                id="sign-in-button"
                color="#A5D867"
                :loading="loading2"
                :disabled="!valid || loading2"
                @click="getVerificationCode"
              >
                {{ $t('loginScreen.getVerificationCode') }}
                <template #loader>
                  {{ $t('common.loading') }}
                  <span class="custom-loader">
                    <v-icon>cached</v-icon>
                  </span>
                </template>
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </div>
      </v-card>
    </v-dialog>

    <!-- Place this near the root of your app, like App.vue or index.html -->
    <div id="recaptcha-container" style="display: none"></div>

    <!-- Unlink Phone Dialog -->
    <v-dialog v-model="unLinkPhoneDialog" persistent max-width="400px">
      <v-card
        :color="$vuetify.theme.dark ? '#2d2d2d' : 'rgba(255,255,255,.95)'"
      >
        <v-card-title v-if="user?.phoneNumber" class="text-h6 pb-0 text-center">
          {{ $t('profile.unlinkTitle', { phone: user.phoneNumber }) }}
        </v-card-title>
        <v-card-subtitle class="v-card-text text-center text-body-2 pa-0 pb-3">
          {{ $t('profile.unlinkConfirm') }}
        </v-card-subtitle>
        <v-card-actions class="justify-center pb-4">
          <v-btn
            class="text-white font-weight-bold text-subtitle-2"
            variant="flat"
            color="#A5D867"
            @click="unLinkPhone"
          >
            {{ $t('profile.unlinkYes') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="#383935"
            class="text-white"
            @click="unLinkPhoneDialog = false"
          >
            {{ $t('navbar.dialogs.logout.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import BaseFileUpload from '@/components/base/BaseFileUpload.vue'
  import { mapState } from 'vuex'
  import { isValidPhoneNumber } from 'libphonenumber-js'
  import { confirmAction } from '@/components/utilities/SwalService'
  import { currentLocale } from '@/helpers/localePath'

  export default {
    name: 'ProfileEditor',
    components: {
      BaseFileUpload,
    },
    props: ['e1'],
    data() {
      return {
        progressUpload: 0,
        url: null,
        user_name: null,
        uid: null,
        fullPath: null,
        oldAvatar: null,
        avatar: null,
        uploadTask: '',
        uploadStarted: false,
        feedback: null,
        movingOn: false,
        saving: false,
        saved: false,
        files: [],
        edit: false,
        tempImages: [],
        isFormValid: false,
        imageURL: null,
        loader: null,
        loading: false,
        loading2: false,
        creating: false,
        codes: [],
        hasImage: false,
        phoneDialog: false,
        verifyPhone: false,
        // phone: "",
        // phoneIsValid: false,
        otp: '',
        valid: false,
        results: {},
        unLinkPhoneDialog: false,
        requestingLeader: false,
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len ||
            this.$t('profile.minCharacters', { count: len }),
          required: (v) =>
            !!v.trim() || this.$t('feedback.validation.required'),
        },
        // phoneRules: [
        //   (v) => !!v || "Phone number is required",
        //   (v) =>
        //     isValidPhoneNumber(v, this.getCountry()) ||
        //     "Enter a valid phone number",
        // ],
      }
    },
    computed: {
      ...mapState('AppData', ['cid']),
      ...mapState('UserData', ['user', 'oldFullPath', 'profileComplete']),
      hasPhoneNumber() {
        return !!this.user?.phoneNumber
      },
      churchId() {
        return this.user?.cid || this.cid || null
      },
      userRoles() {
        const fromUser = Array.isArray(this.user?.roles) ? this.user.roles : []
        const fromStore = this.$store.getters['UserData/getRoles']
        return fromUser.length
          ? fromUser
          : Array.isArray(fromStore)
            ? fromStore
            : []
      },
      isPrivilegedForLeaderRequest() {
        return this.userRoles.some((role) =>
          ['leader', 'admin', 'superAdmin', 'helpDesk'].includes(role)
        )
      },
      showLeaderRequest() {
        return !!(
          this.e1 &&
          this.user?.id &&
          this.churchId &&
          !this.isPrivilegedForLeaderRequest
        )
      },
      canRequestLeader() {
        return this.showLeaderRequest && this.user?.possibleLeader !== true
      },
    },
    watch: {
      user: {
        handler: function (val) {
          if (val) {
            this.user_name = val.user_name || '' // Ensure it's a string
            this.fullPath = val.fullPath || ''
            this.url = val.url || ''
          } else {
            this.user_name = '' // Reset on logout
            this.fullPath = ''
            this.url = ''
          }
        },
        deep: true,
      },
      phoneDialog(val) {
        if (val) {
          setTimeout(() => {
            this.$nextTick(() => {
              if (
                !window.recaptchaVerifier ||
                !window.recaptchaVerifierWidgetId
              ) {
                this.initRecaptchaVerifier()
              }
            })
          }, 100) // ← short delay ensures dialog DOM fully injected
        }
      },
      uploadTask: function () {
        this.uploadTask.on(
          'state_changed',
          (sp) => {
            this.progressUpload = Math.floor(
              (sp.bytesTransferred / sp.totalBytes) * 100
            )
          },
          (err) => {
            console.error('Avatar upload failed:', err)
            this.saving = false
            this.loading = false
            this.loader = null
            this.uploadStarted = false
            this.feedback = this.$t('profile.uploadFailed')
          },
          () => {
            this.uploadTask.snapshot.ref
              .getDownloadURL()
              .then((downloadURL) => {
                this.url = downloadURL
                this.saving = false
                this.saved = true
                this.detectImageInfo()
              })
          }
        )
      },
      loader() {
        const l = this.loader
        this[l] = !this[l]
        this.loader = null
      },
    },
    methods: {
      // onPhoneInput(payload) {
      //   this.results = payload;
      // },
      // validatePhone() {
      //   // basic validation (you can also use libphonenumber-js to validate properly if needed)
      //   this.phoneIsValid = this.phone && this.phone.length > 6;
      // },
      getPhoneRules() {
        return [
          (v) => !!v || this.$t('loginScreen.phoneRequired'),
          (v) =>
            isValidPhoneNumber(v, this.selectedCountry || 'US') ||
            this.$t('loginScreen.phoneInvalid'),
        ]
      },
      initFileUpload() {
        // document.getElementsByClassName('fileinput')[0].click()
      },
      inputFilter(newFile, oldFile, prevent) {
        if (this.fullPath) {
          this.progressUpload = 0
          this.uploadStarted = false
          this.saved = false
        }

        if (newFile && !oldFile) {
          if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
            return prevent()
          }
        }
        if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
          newFile.url = ''
          let URL = window.URL || window.webkitURL
          if (URL && URL.createObjectURL) {
            newFile.url = URL.createObjectURL(newFile.file)
          }
        }
        this.feedback = null
      },
      getImage() {
        // console.log('getImage ', this.avatar)
        this.upload(this.avatar)
        this.saving = true
      },
      submitPhoneNumberAuthCode(otp) {
        // console.log('submitPhoneNumberAuthCode called ', otp)
        setTimeout(() => {
          this.$store.dispatch('UserData/submitPhoneNumberAuthCode', {
            otp: otp,
          })
        }, 1000)
      },
      openPhoneDialog() {
        this.verifyPhone = false
        this.phone = ''
        this.otp = ''
        this.valid = false
        this.phoneDialog = true
        this.feedback = ''
      },
      initRecaptchaVerifier() {
        const oldContainer = document.getElementById('recaptcha-container')

        // 🔥 Full cleanup: remove old container
        if (oldContainer) {
          oldContainer.remove()
        }

        // 🧱 Recreate fresh container
        const newContainer = document.createElement('div')
        newContainer.id = 'recaptcha-container'
        newContainer.style.display = 'none'
        document.body.appendChild(newContainer)

        try {
          window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
            'recaptcha-container',
            {
              size: 'invisible',
              callback: () => {
                // console.log("✅ reCAPTCHA solved:", response);
              },
              'expired-callback': () => {
                console.warn('⚠️ reCAPTCHA expired.')
                window.recaptchaVerifierWidgetId = undefined
              },
            }
          )

          window.recaptchaVerifier.render().then((widgetId) => {
            window.recaptchaVerifierWidgetId = widgetId
            // console.log("✅ reCAPTCHA widget rendered:", widgetId);
          })
        } catch (e) {
          console.error('🔥 reCAPTCHA init failed:', e)
        }
      },
      async getVerificationCode() {
        try {
          // 🧼 Always clear verifier
          if (window.recaptchaVerifier) {
            try {
              window.recaptchaVerifier.clear?.()
            } catch (e) {
              console.warn('⚠️ recaptchaVerifier.clear failed:', e)
            }
          }

          window.recaptchaVerifier = null
          window.recaptchaVerifierWidgetId = undefined

          // 🔁 Recreate container
          this.resetRecaptchaContainer()

          // ⏳ Wait a tick for DOM to settle
          await this.$nextTick()

          // 🧱 Create new verifier
          window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
            'recaptcha-container',
            {
              size: 'invisible',
              callback: () => {
                // console.log("✅ reCAPTCHA solved:", response);
              },
              'expired-callback': () => {
                console.warn('⚠️ reCAPTCHA expired.')
                window.recaptchaVerifierWidgetId = undefined
              },
            }
          )

          // 🧪 Wait for full render before use
          const widgetId = await window.recaptchaVerifier.render()
          window.recaptchaVerifierWidgetId = widgetId
          // console.log("✅ reCAPTCHA rendered:", widgetId);

          // 📲 Send the code
          this.$store.dispatch('UserData/userGetPhoneVerificationCodeForLink', {
            phone: this.phone,
            appVerifier: window.recaptchaVerifier,
          })
        } catch (e) {
          console.error('🔥 getVerificationCode error:', e)
          this.feedback = this.$t('profile.phoneVerifyFailed')
        }
      },
      unLinkPhone() {
        // we set a delay so the user sees something happen when they click the button
        setTimeout(() => {
          this.$store.dispatch('UserData/unLinkPhoneFromAccount', {
            phone: this.user.phoneNumber,
          })
        }, 1000)
      },
      resetRecaptchaContainer() {
        // 🔁 Fully remove and recreate the reCAPTCHA DOM container
        const existing = document.getElementById('recaptcha-container')
        if (existing) {
          existing.remove()
        }

        const fresh = document.createElement('div')
        fresh.id = 'recaptcha-container'
        fresh.style.display = 'none'
        document.body.appendChild(fresh)
      },
      upload(avatar) {
        var splitavatar = avatar.name.split('.')
        var newname = splitavatar[0] + '_' + Date.now() + '.' + splitavatar[1]
        var storage = firebase.storage()
        this.progressUpload = ''
        this.uploadStarted = true
        this.uploadTask = storage
          .ref('avatars/' + this.cid + '/' + newname)
          .putString(this.imageURL, 'data_url')
        this.fullPath = 'avatars/' + this.cid + '/' + newname

        this.tempImages.push(this.fullPath)
      },
      setImage() {
        this.editor = true
        this.disabled = true
        this.loader = 'loading'
        this.atLocation = false
        this.progressUpload = 0

        if (this.user.fullPath) {
          this.$store.dispatch('UserData/updateOldFullPath', this.user.fullPath)
          this.oldAvatar = this.user.fullPath
        }

        let oldFile = this.files[0]
        var imgCanvas = document.createElement('canvas')
        imgCanvas.width = this.$refs.editImage.width
        imgCanvas.height = this.$refs.editImage.height
        imgCanvas.getContext('2d').drawImage(this.$refs.editImage, 0, 0)

        let binStr = atob(imgCanvas.toDataURL(oldFile.type).split(',')[1])
        let arr = new Uint8Array(binStr.length)
        for (let i = 0; i < binStr.length; i++) {
          arr[i] = binStr.charCodeAt(i)
        }
        let imgFile = new File([arr], oldFile.name, { type: oldFile.type })
        this.avatar = imgFile

        const file = this.avatar
        if (!file.type.includes('image/')) {
          console.log('Please select an image file')
          return
        }

        if (typeof FileReader === 'function') {
          const reader = new FileReader()
          reader.onload = (event) => {
            this.imageURL = event.target.result
            this.getImage()
          }
          reader.readAsDataURL(file)
        } else {
          console.log('Sorry, FileReader API not supported')
        }
      },
      detectImageInfo() {
        this.feedback = ''
        this.loader = null
        this.loading = false

        this.updateProfile()
      },
      deleteImage(fullPath) {
        // console.log('delete ', fullPath, fullPath.split('/')[1])
        let storage = firebase.storage()
        let storageRef = storage.ref()

        // if(fullPath.split('/')[1] !== 'static') {
        // Create a reference to the file we want to delete
        let imageRef = storageRef.child(fullPath)
        imageRef
          .delete()
          .then(() => {
            // this.reset()
            this.progressUpload = 0
            this.uploadStarted = false
            this.saved = false
            // console.log('avatar deleted ', this.avatar, this.saved)
          })
          .catch((error) => {
            console.error(`file delete error occured: ${error}`)
          })
        // }
      },
      reset() {
        if (this.oldAvatar) this.deleteImage(this.oldAvatar)

        this.url = ''
        this.fullPath = ''
        this.progressUpload = 0
        this.uploadStarted = false
        this.avatar = null
        this.saved = false
        this.saving = false
        this.imageURL = null
        this.hasImage = false
        this.files = []
        this.edit = false
        this.feedback = ''
      },
      async updateProfile() {
        this.feedback = ''
        this.creating = true

        try {
          const trimmedName = (this.user_name || '').trim()
          this.user_name = trimmedName || null

          this.url = this.url ? this.url : null
          this.fullPath = this.fullPath ? this.fullPath : null

          const profileComplete = this.user_name !== null

          if (!this.user?.id) {
            throw new Error('User record not found')
          }

          if (this.oldAvatar) this.deleteImage(this.oldAvatar)

          if (this.tempImages.length > 1) {
            this.tempImages.pop()
            this.tempImages.forEach((element) => {
              this.deleteImage(element)
            })
          }

          await db.collection('users').doc(this.user.id).update({
            user_name: this.user_name,
            url: this.url,
            fullPath: this.fullPath,
            profileComplete: profileComplete,
          })

          this.oldAvatar = null
          this.tempImages = []
          this.$store.dispatch('UserData/updateOldFullPath', this.oldAvatar)
          this.$store.dispatch(
            'UserData/updateProfileComplete',
            profileComplete
          )
          this.$store.dispatch('UserData/updateUser', {
            cid: this.cid,
            url: this.url,
            uid: this.uid,
            fullPath: this.fullPath,
            user_name: this.user_name,
            active: this.user.active,
            activated: this.user.activated,
            id: this.user.id,
            phoneNumber: this.user.phoneNumber,
          })

          const userStateSnap = await db
            .collection('userstate')
            .where('cid', '==', this.cid)
            .where('email', '==', this.user.email)
            .get()

          if (userStateSnap.empty) {
            await db.collection('userstate').add({
              email: this.user.email,
              unit: '0',
              user_name: this.user_name,
              url: this.url,
              cid: this.cid,
              status: 'active',
              timestamp: Date.now(),
            })
          } else {
            await Promise.all(
              userStateSnap.docs.map((doc) =>
                doc.ref.update({
                  url: this.url,
                  user_name: this.user_name,
                })
              )
            )
          }

          this.files = []
          this.movingOn = true
          this.feedback = this.$t('profile.updated')

          if (this.url === null || this.url == '') {
            this.feedback += '<br>You still need to upload an avatar.'
          }
          if (this.user_name === null || this.user_name == '') {
            this.feedback += '<br>You still need to add a username.'
          }
        } catch (err) {
          console.error('Profile update failed:', err)
          this.feedback = this.$t('profile.updateFailed')
        } finally {
          this.creating = false
        }
      },
      async requestLeader() {
        if (!this.canRequestLeader || this.requestingLeader) return

        const confirmed = await confirmAction({
          title: this.$t('profile.leaderRequest.title'),
          html: this.$t('profile.leaderRequest.confirmHtml'),
          confirmButtonText: this.$t('profile.leaderRequest.confirmButton'),
          cancelButtonText: this.$t('common.cancel'),
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })
        if (!confirmed) return

        this.requestingLeader = true
        this.feedback = null
        try {
          await db.collection('users').doc(this.user.id).update({
            possibleLeader: true,
          })
          this.$store.dispatch('UserData/updateUser', { possibleLeader: true })

          const displayName = (
            this.user_name ||
            this.user.user_name ||
            ''
          ).trim()
          const email = this.user.email || ''
          const sourceLocale = currentLocale()

          try {
            await db.collection('notifications').add({
              active: true,
              cid: this.churchId,
              notification: this.$t('profile.leaderRequest.adminNotification', {
                name: displayName || email,
                email,
              }),
              audience: ['admin'],
              messagetype: 'warning',
              submittedby: email,
              created: Date.now(),
              autoTranslate: true,
              sourceLocale,
            })
          } catch (notifyErr) {
            console.error('Failed to notify church admin:', notifyErr)
          }

          this.feedback = this.$t('profile.leaderRequest.success')
        } catch (err) {
          console.error('Failed to request leader:', err)
          this.feedback = this.$t('profile.leaderRequest.error')
        } finally {
          this.requestingLeader = false
        }
      },
    },
    created() {
      this.$store.watch(
        () => {
          return this.$store.getters['UserData/getLoginFeedback'] // could also put a Getter here
        },
        (newValue) => {
          // console.log('newValue', newValue)
          //something changed do something
          if (newValue) {
            if (
              newValue.code &&
              (newValue.code == 'resubmit-verification' ||
                newValue.code == 'no-verfied email')
            ) {
              this.phoneDialog = false
            }
            if (newValue.code && newValue.code == 'phone-verified') {
              this.verifyPhone = true
            }
            if (newValue.code && newValue.code == 'signed in') {
              this.verifyPhone = false
              this.phoneDialog = false
            }
            if (newValue.code && newValue.code == 'unlinked-number') {
              this.unLinkPhoneDialog = false
            }
            if (newValue.code && newValue.code == 'linked-number') {
              this.phoneDialog = false
            }
            this.feedback = newValue.message
            this.loader = null
            this.loading = false
            this.loading2 = false
          }
        },
        //Optional Deep if you need it
        {
          deep: true,
        }
      )
      if (this.user) {
        this.user_name = this.user.user_name
        this.fullPath = this.user.fullPath
        this.url = this.user.url
        this.uid = this.user.uid
      }
      if (this.oldFullPath) {
        this.oldAvatar = this.oldFullPath
      }
      firebase
        .auth()
        .currentUser?.reload()
        .then(() => {
          const phone = firebase.auth().currentUser?.phoneNumber || ''

          this.$store.commit('UserData/setUser', {
            ...this.user,
            phoneNumber: phone,
          })

          // console.log("📲 Forced Firebase reload, phone:", phone);
        })
    },
  }
</script>

<style>
  .user-input input[type='text']:not(.browser-default),
  .user-input input[type='text']:not(.browser-default):focus:not([readonly]) {
    border: none;
    box-shadow: none;
  }
  .user-input .v-icon.mdi-close {
    font-size: 16px;
  }
  .profile-editor-container #fileInput {
    display: none;
  }
  .profile-editor-container .avatar-upload {
    width: 100%;
  }
  .profile-editor-container .v-avatar {
    border-radius: 50%;
  }
  .profile-editor-container .v-avatar label {
    width: 100%;
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    cursor: pointer;
  }
  .profile-editor-container .v-avatar img {
    object-fit: cover;
  }
  .profile-editor-container .file-uploads label {
    cursor: pointer;
  }
  .btn-floating.pulse {
    width: auto;
    line-height: 1;
  }
  .avatar-edit-image img {
    max-width: 100%; /* This rule is very important, please do not ignore this! */
  }
  .profile-editor-container .v-card {
    max-height: inherit;
  }
  .profile-editor-container .text-center .btn {
    margin: 0 0.5rem;
  }
  .profile-editor-container .avatar-edit-image {
    max-width: 90% !important;
    margin: auto;
  }
  .profile-editor-container .drop-active {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    position: fixed;
    z-index: 9999;
    opacity: 0.6;
    text-align: center;
    background: #000;
  }
  .profile-editor-container .drop-active h3 {
    margin: -0.5em 0 0;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    -webkit-transform: translateY(-50%);
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
    font-size: 40px;
    color: #fff;
    padding: 0;
  }
  .profile-editor-container .found-labels {
    text-align: left;
    margin: 10px;
  }
  .profile-editor-container .file-uploads {
    display: inline-flex;
  }
  .profile-editor-container .leader-request-btn {
    letter-spacing: 0.06em;
    padding-inline: 20px;
  }
  .profile-editor-container .pulse-button {
    box-shadow: 0 0 0 0 rgba(165, 216, 103, 0.7);
    -webkit-animation: green-pulse 3s infinite cubic-bezier(0.66, 0, 0, 1);
    -moz-animation: green-pulse 3s infinite cubic-bezier(0.66, 0, 0, 1);
    -ms-animation: green-pulse 3s infinite cubic-bezier(0.66, 0, 0, 1);
    animation: green-pulse 3s infinite cubic-bezier(0.66, 0, 0, 1);
  }
  .grecaptcha-badge {
    visibility: hidden;
  }
  @-webkit-keyframes green-pulse {
    20%,
    100% {
      box-shadow: 0 0 0 30px rgba(165, 216, 103, 0);
    }
  }
  @-moz-keyframes green-pulse {
    20%,
    100% {
      box-shadow: 0 0 0 30px rgba(165, 216, 103, 0);
    }
  }
  @-ms-keyframes green-pulse {
    20%,
    100% {
      box-shadow: 0 0 0 30px rgba(165, 216, 103, 0);
    }
  }
  @keyframes green-pulse {
    20%,
    100% {
      box-shadow: 0 0 0 30px rgba(165, 216, 103, 0);
    }
  }
</style>
